import React from 'react';
import { Box, Typography, Stack, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';

/**
 * IntegrationHealthCard — sekcja 8.13 dokumentu UI Design Direction.
 * Kompaktowa karta statusu integracji, dopasowana do wąskich kontenerów
 * (np. panel boczny) — bez zawijających się etykiet i "gołych" znaczników czasu.
 */
function IntegrationHealthCard({ overallOk = true, lastSyncLabel, integrations = [], onManage }) {
  const theme = useTheme();
  const tokens = theme.tokens?.components?.integrationHealthCard;
  const visibleIntegrations = integrations.slice(0, 3);
  const extraCount = integrations.length - visibleIntegrations.length;

  return (
    <Box
      onClick={onManage}
      role={onManage ? 'button' : undefined}
      sx={{
        borderRadius: `${tokens?.radius ?? 20}px`,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        p: 2,
        cursor: onManage ? 'pointer' : 'default',
        transition: 'border-color 150ms ease, background-color 150ms ease',
        '&:hover': onManage
          ? { borderColor: 'primary.main', bgcolor: theme.tokens?.semantic?.surface?.brandTint }
          : {},
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
        <ExtensionOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Integracje
        </Typography>
        {onManage && <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
        {overallOk ? (
          <CheckCircleIcon sx={{ color: tokens?.okColor, fontSize: 16, flexShrink: 0 }} />
        ) : (
          <ErrorOutlineIcon sx={{ color: tokens?.warnColor, fontSize: 16, flexShrink: 0 }} />
        )}
        <Typography
          variant="caption"
          noWrap
          sx={{ fontWeight: 650, color: overallOk ? 'success.main' : 'warning.main' }}
        >
          {overallOk ? 'Wszystko działa' : 'Wymaga uwagi'}
        </Typography>
      </Stack>

      {visibleIntegrations.length > 0 && (
        <Stack spacing={0.75} sx={{ mb: 1.25 }}>
          {visibleIntegrations.map((integration) => (
            <Stack
              key={integration.name}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography
                variant="caption"
                noWrap
                sx={{ fontWeight: 500, color: 'text.secondary' }}
              >
                {integration.name}
              </Typography>
              <Chip
                label={integration.status}
                size="small"
                color={integration.ok ? 'success' : 'default'}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.6875rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            </Stack>
          ))}
          {extraCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              +{extraCount} więcej
            </Typography>
          )}
        </Stack>
      )}

      {lastSyncLabel && (
        <Typography variant="caption" color="text.disabled" noWrap sx={{ display: 'block' }}>
          {lastSyncLabel}
        </Typography>
      )}
    </Box>
  );
}

export default IntegrationHealthCard;
